import { useCallback, useEffect, useRef, useState } from "react";
import { appendActivityPage, mergeLatestPage } from "../../utils/userActivity";

export const PAGE_SIZE = 20;
const HIGHLIGHT_MS = 2500;

/**
 * Son İşlemler akışı (#308). Panel kapanınca SİLİNMEZ — durum menüdeki düğmede
 * yaşar. `refresh` (panel her açıldığında): liste boşsa ilk sayfa; doluysa ilk
 * sayfa arka planda istenir ve yalnız yeni kayıtlar ÜSTE eklenir (kısa süre
 * vurgulanır). `loadMore`: imleçle daha eskiler. Kullanıcı değişince sıfırlanır.
 */
export default function useActivityFeed({ fetchPage, token, userKey }) {
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialLoading, setInitialLoading] = useState(false);
  const [isRefreshing, setRefreshing] = useState(false);
  const [isLoadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [highlightIds, setHighlightIds] = useState(() => new Set());

  // Güncel değerler ref'te: art arda gelen çağrılar eski kopyayı okumasın
  const stateRef = useRef({ items: [], cursor: null, hasMore: true });
  const busyRef = useRef(false);
  const generationRef = useRef(0);
  const highlightTimerRef = useRef(null);
  const lastFailedRef = useRef("latest");

  const commit = useCallback((next) => {
    stateRef.current = { items: next.items, cursor: next.cursor, hasMore: next.hasMore };
    setItems(next.items);
    setHasMore(next.hasMore);
  }, []);

  // Kullanıcı değişince (çıkış / başka hesap) liste sıfırlanır; uçuştaki
  // isteğin cevabı yeni kullanıcının listesine karışmaz
  useEffect(() => {
    generationRef.current += 1;
    busyRef.current = false;
    commit({ items: [], cursor: null, hasMore: true });
    setError(null);
    setHighlightIds(new Set());
    setInitialLoading(false);
    setRefreshing(false);
    setLoadingMore(false);
  }, [userKey, commit]);

  useEffect(() => () => clearTimeout(highlightTimerRef.current), []);

  const run = useCallback(
    async (kind) => {
      if (busyRef.current || !token) return;
      const gen = generationRef.current;
      const first = stateRef.current.items.length === 0;
      if (kind === "more" && (first || !stateRef.current.hasMore)) return;
      busyRef.current = true;
      setError(null);
      const setBusy = kind === "more" ? setLoadingMore : first ? setInitialLoading : setRefreshing;
      setBusy(true);
      try {
        const res = await fetchPage({
          token,
          limit: PAGE_SIZE,
          ...(kind === "more" ? { before: stateRef.current.cursor } : {}),
        }).unwrap();
        if (gen !== generationRef.current) return;
        const page = res?.data || {};
        if (kind === "more") {
          commit({
            items: appendActivityPage(stateRef.current.items, page.items || []),
            cursor: page.nextCursor || null,
            hasMore: !!page.hasMore,
          });
          return;
        }
        const merged = mergeLatestPage(stateRef.current, page);
        commit(merged);
        if (merged.addedIds.length > 0) {
          setHighlightIds(new Set(merged.addedIds));
          clearTimeout(highlightTimerRef.current);
          highlightTimerRef.current = setTimeout(() => setHighlightIds(new Set()), HIGHLIGHT_MS);
        }
      } catch (err) {
        if (gen !== generationRef.current) return;
        lastFailedRef.current = kind;
        setError(err?.data?.message || "Son işlemler alınamadı.");
      } finally {
        if (gen === generationRef.current) {
          busyRef.current = false;
          setBusy(false);
        }
      }
    },
    [fetchPage, token, commit]
  );

  const refresh = useCallback(() => run("latest"), [run]);
  const loadMore = useCallback(() => run("more"), [run]);
  // Son başarısız isteği tekrarla (üste ekleme ya da daha eskiler)
  const retry = useCallback(() => run(lastFailedRef.current), [run]);

  return {
    items,
    hasMore,
    isInitialLoading,
    isRefreshing,
    isLoadingMore,
    error,
    highlightIds,
    refresh,
    loadMore,
    retry,
  };
}
